import React, { useLayoutEffect, useRef } from "react";
import { Messages as IMessages } from "Types/api/Messages";
import Message from "src/components/Messaging/Messages/Message";
import Sending from "src/components/Messaging/Messages/Sending";
import Failed from "src/components/Messaging/Messages/Failed";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

interface Props {
    messages: IMessages;
    sending: string[];
    failed: string[];
    retry: () => void;
}

const MessagesDiv = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 1rem;
    padding-bottom: 0.5rem;
    flex-shrink: 2;
`;

const DateHeader = styled.div`
    font-weight: 500;
`;

export const Messages = ({ messages, sending, failed, retry }: Props) => {
    const { t } = useTranslation();

    const messagesDivRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        messagesDivRef?.current?.scrollTo &&
            messagesDivRef.current.scrollTo({ top: messagesDivRef.current.scrollHeight });
    }, [messages, sending, failed]);

    const messagesGroupedByDate = groupBy(messages, (message) => new Date(message.displayDate).toDateString());
    const sortedDateKeys = sortBy(Object.keys(messagesGroupedByDate), (key) => new Date(key));

    return (
        <React.Fragment>
            <MessagesDiv ref={messagesDivRef}>
                {sortedDateKeys.map((date) => (
                    <React.Fragment key={date}>
                        <DateHeader>
                            {date === new Date().toDateString()
                                ? t("MessageCenter.Messages.today")
                                : `${new Date(messagesGroupedByDate[date][0].displayDate).toLocaleDateString(
                                      undefined,
                                      {
                                          weekday: "short",
                                          month: "short",
                                          day: "numeric",
                                      }
                                  )}`}
                        </DateHeader>

                        {messagesGroupedByDate[date].reverse().map((message, index) => (
                            <Message
                                message={message}
                                key={index}
                                displayTime={
                                    messagesGroupedByDate[date].length - 1 === index ||
                                    messagesGroupedByDate[date][index + 1].user?.name !==
                                        messagesGroupedByDate[date][index].user?.name
                                }
                            />
                        ))}
                    </React.Fragment>
                ))}

                {sortedDateKeys.length === 0 && <DateHeader>Today</DateHeader>}

                {sending.map((message, index) => (
                    <Sending message={message} key={index} />
                ))}

                {failed.map((message, index) => (
                    <Failed message={message} retry={retry} key={index} />
                ))}
            </MessagesDiv>
        </React.Fragment>
    );
};

export default Messages;
